import { useEffect, useState } from "react";
import * as Yup from "yup";
import { Link as RouterLink } from "react-router-dom";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
// routes
import { PATH_AUTH } from "../../routes/paths";
// auth
import { useAuthContext } from "../../auth/useAuthContext";
// components
import Iconify from "../../components/iconify";
import FormProvider, { RHFTextField } from "../../components/hook-form";
import { requestNotificationPermission } from "src/firebase/request-notification-permission";
import NotificationsPermissionDialog from "../dashboards/notifications/notifications-permission-dialog";

// ----------------------------------------------------------------------

type FormValuesProps = {
  email: string;
  password: string;
  afterSubmit?: string;
};

export default function AuthLoginForm() {
  const { login } = useAuthContext();

  const [showPassword, setShowPassword] = useState(false);
  const [fcmToken, setFcmToken] = useState<string>();

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const defaultValues = {
    email: "",
    password: "",
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  useEffect(() => {
    const initializeFCM = async () => {
      try {
        const token: string | null = (await requestNotificationPermission()) || "";

        if (token) {
          setFcmToken(token);
        }
      } catch (error) {
        console.error("Failed to retrieve FCM token:", error);
      }
    };

    initializeFCM();
  }, []);

  const onSubmit = async (data: FormValuesProps) => {
    try {
      await login(data.email, data.password);
    } catch (error) {
      reset();
      setError("afterSubmit", {
        ...error,
        message: error.message || error,
      });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}
        <RHFTextField
          name="email"
          label="Email address"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end">
                  <Iconify icon="eva:email-outline" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitSuccessful || isSubmitting}
          sx={{
            bgcolor: "#87D7AC",
            fontSize: "18px",
            // bgcolor: 'text.primary',
            color: (theme) => (theme.palette.mode === "light" ? "common.white" : "grey.800"),
            // '&:hover': {
            //   bgcolor: 'text.primary',
            //   color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
            // },
          }}
        >
          Sign In
        </LoadingButton>
      </Stack>

      <Stack alignItems="center" marginTop={2}>
        <Typography variant="body1" fontFamily="spectral" fontWeight={400}>
          Don’t have an account?
          <Link color="#5DB1BA" to={PATH_AUTH.register} component={RouterLink} variant="subtitle2">
            Sign up
          </Link>
        </Typography>
      </Stack>

      <NotificationsPermissionDialog />
    </FormProvider>
  );
}
